'use client';

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from 'next-themes';

type Theme = "light" | "dark" | "system";

const ThemeToggle: React.FC = () => {
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme } = useTheme();
  
  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="w-9 h-9" />
    );
  }

  const currentTheme = theme as Theme;
  
  // Toggle between light, dark, and system themes
  const toggleTheme = () => {
    if (currentTheme === 'system') {
      setTheme('light');
    } else if (currentTheme === 'light') {
      setTheme('dark');
    } else {
      setTheme('system');
    }
  };

  // Get the current theme icon
  const getThemeIcon = () => {
    if (currentTheme === 'dark') {
      return <Moon className="w-5 h-5 text-yellow-300" />;
    } else if (currentTheme === 'light') {
      return <Sun className="w-5 h-5 text-orange-500" />;
    } else {
      return <Monitor className="w-5 h-5 text-blue-500" />;
    }
  };

  // Get the next theme label
  const getNextThemeLabel = () => {
    if (currentTheme === 'system') return 'Switch to light mode';
    if (currentTheme === 'light') return 'Switch to dark mode';
    return 'Switch to system theme';
  };

  // Animation variants
  const variants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.8, opacity: 0 },
  };

  return (
    <motion.button
      className="relative p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50"
      onClick={toggleTheme}
      aria-label={getNextThemeLabel()}
      initial={false}
      whileTap={{ scale: 0.9 }}
      type="button"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentTheme}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={variants}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center w-5 h-5"
        >
          {getThemeIcon()}
        </motion.div>
      </AnimatePresence>
    </motion.button>
  );
};

export default ThemeToggle;